const TOPIC_COLOR_MAP = {
  'Guest Room': '#7b9af1',
  'Services': '#21c55e',
  'Brand': '#0ba5e9',
  'Food & Beverage': '#936e79',
  'Property/Facilities': '#f89753',
  'Staff': '#a755f7',
  'Pest': '#7071cc'
};

const getTopicColor = (() => {
  const cache = new Map();
  return (rootTopic) => {
    if (cache.has(rootTopic)) return cache.get(rootTopic);
    const color = TOPIC_COLOR_MAP[rootTopic] || '#cccccc';
    cache.set(rootTopic, color);
    return color;
  };
})();

const getRootTopicSafe = (topic, hierarchyMap, maxDepth = 500) => {
  let current = topic;
  let depth = 0;
  const visited = new Set([topic]);

  while (depth < maxDepth) {
    if (!hierarchyMap.has(current)) {
      return current;
    }

    const parentData = hierarchyMap.get(current);
    const parentTopics = Array.isArray(parentData?.parent_topic) 
      ? parentData.parent_topic 
      : (parentData?.parent_topic ? [parentData.parent_topic] : []);

    if (parentTopics.length === 0) {
      return current;
    }

    const parent = parentTopics[0];

    if (visited.has(parent)) {
      return current;
    }

    visited.add(parent);
    current = parent;
    depth++;
  }

  return current;
};


const sanitizeHierarchy = (hierarchy, hierarchyMap) => {
  const issues = {
    circularReferences: [],
    orphanedNodes: [],
    selfReferences: [],
    validItems: 0,
    removedItems: 0
  };

  const cleanedHierarchy = hierarchy.filter(h => {
    const topic = h.topic;
    const parentTopics = Array.isArray(h.parent_topic) 
      ? h.parent_topic 
      : (h.parent_topic ? [h.parent_topic] : []);

    if (parentTopics.includes(topic)) {
      issues.selfReferences.push(topic);
      issues.removedItems++;
      return false;
    }

    for (const parentTopic of parentTopics) {
      let current = parentTopic;
      const visited = new Set([topic]);
      let depth = 0;
      const maxDepth = 500;

      while (current && depth < maxDepth) {
        if (current === topic) {
          issues.circularReferences.push({
            topic,
            parent: parentTopic,
            details: `Creates cycle back to "${topic}"`
          });
          issues.removedItems++;
          return false;
        }

        visited.add(current);
        const parentData = hierarchyMap.get(current);
        const nextParents = Array.isArray(parentData?.parent_topic)
          ? parentData.parent_topic
          : (parentData?.parent_topic ? [parentData.parent_topic] : []);

        if (nextParents.length === 0) {
          break;
        }

        const nextParent = nextParents[0];

        if (visited.has(nextParent)) {
          break; 
        }

        current = nextParent;
        depth++;
      }
    }

    issues.validItems++;
    return true;
  });

  return { cleanedHierarchy, issues };
};

const expandMultiParentItems = (data) => {
  const expanded = [];
  const multiParentMap = new Map();

  data.forEach(item => {
    const parentTopics = Array.isArray(item.parent_topic)
      ? item.parent_topic
      : (item.parent_topic ? [item.parent_topic] : []);

    if (parentTopics.length === 0) {
      expanded.push(item);
      return;
    }

    if (parentTopics.length === 1) {
      expanded.push(item);
      return;
    }

    if (!multiParentMap.has(item.topic)) {
      multiParentMap.set(item.topic, parentTopics.length);
    }

    parentTopics.forEach((parent, idx) => {
      const newItem = {
        ...item,
        parent_topic: parent,
        _multiParentIndex: idx,
        _totalParents: parentTopics.length,
        _originalTopic: item.topic
      };
      expanded.push(newItem);
    });
  });



  return expanded;
};

const validateParentReferences = (data, hierarchyMap) => {
  const validParents = new Set(['Topics']);
  
  data.forEach(item => {
    if (item.id && item.id !== 'Topics') {
      validParents.add(item.id);
    }
  });

  const fixedData = data.map(item => {
    if (item.parent && !validParents.has(item.parent)) {
      return { ...item, parent: 'Topics' };
    }
    return item;
  });

  return fixedData;
};

const removeDuplicates = (data) => {
  const seen = new Map(); 
  const uniqueData = [];

  data.forEach(item => {
    const count = (seen.get(item.id) || 0) + 1;
    seen.set(item.id, count);

    if (item._multiParentIndex !== undefined) {
      const uniqueId = `${item.id}_[parent:${item.parent}]`;
      uniqueData.push({
        ...item,
        id: uniqueId,
        _originalId: item.id
      });
    } else {
      uniqueData.push(item);
    }
  });

  return uniqueData;
};

export const transformTopicsData = (data, options = {}) => {
  const {
    maxHierarchyDepth = 500,
    batchSize = 1000,
    onProgress = null
  } = options;

  const { rows, metric_data, metric_count, hierarchy } = data;

  if (hierarchy.length === 0) {
    return [];
  }

  const metricMap = new Map();
  const hierarchyMap = new Map();

  for (let i = 0; i < rows.length; i++) {
    if (metric_count[i]) {
      metricMap.set(rows[i], {
        value: metric_count[i][0],
        positivePct: metric_data[i]?.[1],
        negativePct: metric_data[i]?.[2],
        positiveCnt: metric_count[i][1],
        negativeCnt: metric_count[i][2]
      });
    }
  }

  for (let i = 0; i < hierarchy.length; i++) {
    const h = hierarchy[i];
    if (h.topic) {
      hierarchyMap.set(h.topic, h);
    }
  }

  const expandedHierarchy = expandMultiParentItems(hierarchy);

  const { cleanedHierarchy, issues } = sanitizeHierarchy(expandedHierarchy, hierarchyMap);


  hierarchyMap.clear();
  for (let i = 0; i < cleanedHierarchy.length; i++) {
    const h = cleanedHierarchy[i];
    if (h.topic) {
      hierarchyMap.set(h.topic, h);
    }
  }

  const rootTopicCache = new Map();
  
  for (let i = 0; i < cleanedHierarchy.length; i++) {
    const h = cleanedHierarchy[i];
    
    if (!h.topic || rootTopicCache.has(h.topic)) {
      continue;
    }

    const root = getRootTopicSafe(h.topic, hierarchyMap, maxHierarchyDepth);
    rootTopicCache.set(h.topic, root);

    if (onProgress && (i + 1) % batchSize === 0) {
      onProgress({
        processed: i + 1,
        total: cleanedHierarchy.length,
        percentage: Math.round(((i + 1) / cleanedHierarchy.length) * 100)
      });
    }
  }

  let topicsData = [];
  topicsData.push({ id: 'Topics', parent: '', name: 'Topics' });

  for (let i = 0; i < cleanedHierarchy.length; i++) {
    const h = cleanedHierarchy[i];
    
    if (!h.topic) continue;

    const metrics = metricMap.get(h._originalTopic || h.topic) || {};
    const rootTopic = rootTopicCache.get(h.topic);
    const color = getTopicColor(rootTopic);
    const topicName = h.topic.split(' - ').pop();

    topicsData.push({
      id: h._originalId ? `${h._originalId}_[parent:${h.parent_topic}]` : h.topic,
      parent: h.parent_topic || 'Topics',
      name: topicName,
      value: metrics?.value,
      color,
      custom: {
        positivePct: metrics.positivePct,
        negativePct: metrics.negativePct,
        positiveCnt: metrics.positiveCnt,
        negativeCnt: metrics.negativeCnt
      }
    });
  }

  topicsData = validateParentReferences(topicsData, hierarchyMap);

  topicsData = removeDuplicates(topicsData);

  return topicsData;
};